import {
  Controller,
  Control,
  FieldValues,
  Path,
  PathValue,
  UnpackNestedValue,
} from 'react-hook-form';

import Checkbox, { CheckboxProps } from 'ui/Checkbox/Checkbox';

/**
 * Controlled Checkbox Props
 * @extends {UI.Checkbox}
 */
type Props<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> = Omit<CheckboxProps, 'name' | 'id' | 'checked' | 'value' | 'onChange'> & {
  /**
   * `control` object provided by invoking `useForm`
   */
  control: Control<TFieldValues>;

  /**
   * The same as an uncontrolled component's `defaultValue`. When passing a boolean value, it will be treated as checkbox input.
   */
  defaultValue?: UnpackNestedValue<PathValue<TFieldValues, TName>>;

  /**
   * Unique name of your input.
   */
  name: TName;
};

const ControlledCheckbox = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  control,
  defaultValue,
  name,
  ...props
}: Props<TFieldValues, TName>): JSX.Element => (
  <Controller
    name={name}
    control={control}
    defaultValue={defaultValue}
    render={({ field: { value, ...field } }) => (
      <Checkbox id={name} checked={value} {...field} {...props} />
    )}
  />
);

export default ControlledCheckbox;
